/**
 * Popup handling for hosted OAuth.
 *
 * The window MUST be opened synchronously inside the click handler. Opening it
 * after `await startConnection()` loses the user-gesture attribution browsers
 * require, and the popup is blocked — the connect flow then silently does
 * nothing. So a blank window is claimed on click and navigated once the server
 * returns the consent URL.
 */

/**
 * Claim a popup during the user gesture. Returns null if blocked outright.
 *
 * The feature string must not contain `noopener`. Per the HTML spec that makes
 * `window.open` return null by design — it severs the handle deliberately — so
 * every call looked blocked, the caller fell back to same-tab navigation, and
 * the chat page was destroyed mid-OAuth. The evidence was the poll count: one
 * request instead of the ~13 a surviving page makes while the user consents.
 *
 * We need the handle: it is how the consent page is navigated and how we know
 * the tab is still ours. `noreferrer` implies `noopener`, so that goes too;
 * neither is doing security work here, because the URL is minted by our own
 * server and the opened origin is Composio's hosted consent page.
 */
export function openConnectWindow() {
  try {
    return window.open('', '_blank', 'popup=yes,width=600,height=760')
  } catch {
    return null
  }
}

/** Send the claimed window to the consent page, or fall back to this tab. */
export function navigateConnectWindow(popup, url) {
  if (!url) {
    closeConnectWindow(popup)
    return false
  }
  if (popup && !popup.closed) {
    popup.location.href = url
    return true
  }
  // Blocked despite the gesture. Same-tab navigation is worse UX but it is a
  // working flow rather than a dead button.
  window.location.assign(url)
  return true
}

export function closeConnectWindow(popup) {
  try {
    if (popup && !popup.closed) popup.close()
  } catch {
    /* cross-origin popups refuse close(); harmless */
  }
}
